import React from "react";
import { Switch as AriaSwitch, SwitchProps as AriaSwitchProps } from "react-aria-components";
import { composeTailwindRenderProps, focusRing } from "../../utils";
import type { Size } from "../../utils/controlStyles";

type SelectionVariant = "primary";
type LegacySelectionVariant = "default" | "brand";

export interface SwitchProps extends Omit<AriaSwitchProps, "children"> {
  children: React.ReactNode;
  /**
   * Deprecated compatibility prop. Switch now has one selected treatment; `default` and `brand` both map to primary.
   * @default "primary"
   */
  variant?: SelectionVariant | LegacySelectionVariant;
  /**
   * Size of the switch
   * @default "md"
   */
  size?: Size;
}

// Size configurations for track and handle
const sizeConfig = {
  sm: {
    track: "h-4 w-7",
    handle: "h-3.5 w-3.5",
    text: "text-xs",
  },
  md: {
    track: "h-5 w-9",
    handle: "h-4 w-4",
    text: "text-sm",
  },
  lg: {
    track: "h-6 w-11",
    handle: "h-5 w-5",
    text: "text-base",
  },
  xl: {
    track: "h-7 w-14",
    handle: "h-6 w-6",
    text: "text-lg",
  },
} as const;

function getTrackClassName({
  isSelected,
  isDisabled,
  size = "md",
}: {
  isSelected: boolean;
  isDisabled: boolean;
  size?: Size;
}) {
  const baseClasses =
    "flex px-px items-center shrink-0 cursor-default rounded-full transition duration-200 ease-in-out shadow-inner border border-transparent";
  const sizeClasses = sizeConfig[size].track;
  const focusRingClasses = focusRing;
  const focusRingColor = "focus-visible:outline-action-primary";

  let selectedClasses = "";
  if (isSelected) {
    selectedClasses = "bg-action-primary forced-colors:!bg-[Highlight] group-pressed:bg-action-primary-hover";
  } else {
    selectedClasses = "bg-border-input group-pressed:bg-border-focus";
  }

  const disabledClasses = isDisabled
    ? "bg-background-muted forced-colors:group-selected:!bg-[GrayText] forced-colors:border-[GrayText]"
    : "";

  return [baseClasses, sizeClasses, focusRingClasses, focusRingColor, selectedClasses, disabledClasses]
    .filter(Boolean)
    .join(" ");
}

function getHandleClassName({
  isSelected,
  isDisabled,
  size = "md",
}: {
  isSelected: boolean;
  isDisabled: boolean;
  size?: Size;
}) {
  const baseClasses =
    "transform rounded-full bg-background-surface outline-none shadow transition duration-200 ease-in-out";
  const sizeClasses = sizeConfig[size].handle;
  const selectedClasses = isSelected ? "translate-x-[100%]" : "translate-x-0";
  const disabledClasses = isDisabled ? "forced-colors:outline-[GrayText]" : "";

  return [baseClasses, sizeClasses, selectedClasses, disabledClasses].filter(Boolean).join(" ");
}

/**
 * Switch
 *
 * Toggle switch component for binary on/off states.
 * Provides an accessible alternative to checkboxes for settings and preferences.
 */
export function Switch({ children, variant: _variant = "primary", size = "md", ...props }: SwitchProps) {
  return (
    <AriaSwitch
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        // `relative` contains react-aria's visually-hidden <input> — see Checkbox.tsx getCheckboxStyles.
        `relative group flex items-center gap-2 ${sizeConfig[size].text} text-text-primary transition disabled:text-text-disabled forced-colors:disabled:text-[GrayText]`
      )}
    >
      {(renderProps) => (
        <>
          <div className={getTrackClassName({ ...renderProps, size })}>
            <span className={getHandleClassName({ ...renderProps, size })} />
          </div>
          {children}
        </>
      )}
    </AriaSwitch>
  );
}
